
import { Options, Vue } from 'vue-class-component';
import { PropType } from 'vue';
import Popper from 'vue3-popper';
import EventBusService from "@/services/EventBusService";
import { CourseEnrollment } from "@/data/course";
import { AppointmentEnrollmentStatesMap } from "@/data/appointments";
import { LoadAllCourseAppointmentsEvent } from "@/data/eventbus";
import UserCourseUpcomingAppointment from "@/components/UserCourseUpcomingAppointment.vue";
import CourseEnrollmentComponent from "@/components/CourseEnrollment.vue";
import CourseActions from "@/components/CourseActions.vue";
import CourseEnrollmentModal from "@/components/modals/CourseEnrollmentModal.vue";
import { formatDate } from "@/utils/helpers";

const contextTypeValues = ['archive'] as const;
type ContextType = typeof contextTypeValues[number];

@Options({
    props: {
        courseEnrollment: {
            type: Object,
            required: true,
        },
        appointmentEnrollmentStates: {
            type: Object,
            required: false,
            default: null,
        },
        context: {
            type: [String, null] as PropType<ContextType|null>,
            required: false,
            default: null,
            validator(val: string|null) {
                return val === null || (contextTypeValues as unknown as string[]).includes(val);
            }
        },
    },
    components: {
        Popper,
        CourseEnrollmentModal,
        CourseActions,
        CourseEnrollmentComponent,
        UserCourseUpcomingAppointment,
    },
    methods: {
        formatDate
    },
})
export default class UserCourseEnrollment extends Vue {
    courseEnrollment!: CourseEnrollment
    appointmentEnrollmentStates!: AppointmentEnrollmentStatesMap|null

    context!: ContextType|null

    loadingMore = false
    showMoreButton = true

    withdrawalModalShown = false

    get course() {
        return this.courseEnrollment.course;
    }

    get description() {
        return this.course?.info_text || this.course?.course_type.description || null;
    }

    get descriptionLines() {
        if( !this.description ) {
            return [];
        }

        return this.description.split("\n");
    }

    get gatheringUrlMap() {
        // see https://developers.google.com/maps/documentation/urls/get-started
        // format: https://www.google.com/maps/search/?api=1&query=51.52683%2C8.59036
        if( !this.course?.gathering_lat || !this.course?.gathering_lng ) {
            return '';
        }
        return `https://www.google.com/maps/search/?api=1&query=${this.course.gathering_lat}%2C${this.course.gathering_lng}`; // %2C is a comma (url encoded)
    }
    get gatheringUrlDirections() {
        // see https://developers.google.com/maps/documentation/urls/get-started
        // format: https://www.google.com/maps/dir/?api=1&destination=51.52683%2C8.59036
        if( !this.course?.gathering_lat || !this.course?.gathering_lng ) {
            return '';
        }
        return `https://www.google.com/maps/dir/?api=1&destination=${this.course.gathering_lat}%2C${this.course.gathering_lng}`; // %2C is a comma (url encoded)
    }

    get isWaitingButCanEnroll() {
        if( this.courseEnrollment.waiting && !this.courseEnrollment.course?.participants_limit_reached ) {
            return true;
        }
        
        if( (this.course?.appointments_upcoming?.length ?? 0) > 0 ) {
            return this.course?.appointments_upcoming?.some((appointment) => {
                if( this.appointmentEnrollmentStates && typeof this.appointmentEnrollmentStates[appointment.id] !== 'undefined' ) {
                    return this.appointmentEnrollmentStates[appointment.id].enrollment?.waiting
                        && this.appointmentEnrollmentStates[appointment.id].enrollment?.appointment_participants_limit_reached === false;
                }
                return false;
            });
        }

        return false;
    }

    moreLoadingStarted(event: LoadAllCourseAppointmentsEvent) {
        if( event.course !== this.courseEnrollment.course_id ) {
            return;
        }

        this.loadingMore = true;
    }
    moreLoadingFinished(event: LoadAllCourseAppointmentsEvent) {
        if( event.course !== this.courseEnrollment.course_id ) {
            return;
        }

        this.loadingMore = false;
        this.showMoreButton = false;
    }

    loadMoreAppointments() {
        EventBusService.$emit('user.courses.appointments.loadAll', {
            course: this.courseEnrollment.course_id
        });
    }

    onEnrollmentStatusChange() {
        this.withdrawalModalShown = false;
        console.log('changed');
    }

    onContactMailClicked() {
        EventBusService.$emit('modals.region.contact.open', {
            region: this.course?.region_id || -1,
        });
    }

    isContext(context: string) {
        return this.context === context;
    }

    mounted() {
        // this.loadData();

        EventBusService.$on('user.courses.appointments.loadAll:loading', this.moreLoadingStarted);
        EventBusService.$on('user.courses.appointments.loadAll:finished', this.moreLoadingFinished);
    }
    beforeUnmount() {
        EventBusService.$off('user.courses.appointments.loadAll:loading', this.moreLoadingStarted);
        EventBusService.$off('user.courses.appointments.loadAll:finished', this.moreLoadingStarted);
    }
}
